import React from 'react';
import { Badge, BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris';
import { fulfillmentLabel } from '../utils/rentalStatus';
import { normalizeRentalItems } from '../utils/rentalItems';

const DAY_MS = 1000 * 60 * 60 * 24;

function startOfDay(date) {
  const value = new Date(date);
  return Date.UTC(value.getFullYear(), value.getMonth(), value.getDate());
}

function daysFromToday(date) {
  return Math.round((startOfDay(date) - startOfDay(new Date())) / DAY_MS);
}

function formatDate(date) {
  return new Date(date).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatAddress(rental) {
  const parts = [rental.address1, rental.address2, rental.zip, rental.city, rental.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'No address provided';
}

function itemLabel(item) {
  const title = item.title || item.productTitle || `Product #${item.productId}`;
  const prefix = item.itemType === 'addon' ? '+ ' : '';
  const quantity = Number(item.quantity) || 1;
  return `${prefix}${title}${quantity > 1 ? ` (x${quantity})` : ''}`;
}

export function RentalItemThumbnails({ items, textVariant = 'bodySm' }) {
  const normalizedItems = normalizeRentalItems(items);
  if (normalizedItems.length === 0) {
    return <Text variant="bodyMd" tone="subdued">No items</Text>;
  }

  return (
    <BlockStack gap="200">
      {normalizedItems.map((item, index) => (
        <InlineStack key={`${item.productId || 'item'}-${index}`} gap="300" blockAlign="center">
          <img
            src={item.productImage || '/placeholder.png'}
            alt=""
            loading="lazy"
            decoding="async"
            onError={(event) => {
              if (!event.currentTarget.src.endsWith('/placeholder.png')) {
                event.currentTarget.src = '/placeholder.png';
              }
            }}
            width={44}
            height={44}
            style={{
              width: 44,
              height: 44,
              objectFit: 'cover',
              borderRadius: 6,
              border: '1px solid #e1e3e5',
              background: '#f6f6f7',
            }}
          />
          <BlockStack gap="050">
            <Text variant={textVariant}>{itemLabel(item)}</Text>
            {item.parentBundleTitle && (
              <Text variant="bodySm" tone="subdued">Part of {item.parentBundleTitle}</Text>
            )}
          </BlockStack>
        </InlineStack>
      ))}
    </BlockStack>
  );
}

function UpcomingBadge({ startDate }) {
  const days = daysFromToday(startDate);
  if (days <= 0) return <Badge tone="critical">Starts today</Badge>;
  if (days === 1) return <Badge tone="warning">Starts tomorrow</Badge>;
  return <Badge tone={days <= 9 ? 'warning' : 'info'}>Starts in {days} days</Badge>;
}

function ReturnBadge({ rental }) {
  const days = daysFromToday(rental.endDate);
  if (rental.status === 'overdue' || days < 0) {
    const overdueDays = Math.abs(days);
    return <Badge tone="critical">Overdue by {overdueDays} day{overdueDays !== 1 ? 's' : ''}</Badge>;
  }
  if (days === 0) return <Badge tone="warning">Due back today</Badge>;
  return <Badge tone="success">Due back in {days} day{days !== 1 ? 's' : ''}</Badge>;
}

function StatusBadge({ status }) {
  const tones = {
    draft: 'info',
    pending: 'attention',
    confirmed: 'success',
    active: 'success',
    overdue: 'critical',
    returned: 'success',
    cancelled: 'warning',
  };
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
  return <Badge tone={tones[status]}>{label}</Badge>;
}

const RentalOverviewCard = ({ rental, mode, onView, statusAction = null, fulfillmentAction = null }) => {
  const customerName = [rental.customerFirstName, rental.customerLastName]
    .filter(Boolean)
    .join(' ') || 'Unknown';

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" gap="300">
          <Text variant="headingSm">#{rental.id} &mdash; {customerName}</Text>
          {mode === 'upcoming'
            ? <UpcomingBadge startDate={rental.startDate} />
            : mode === 'active'
              ? <ReturnBadge rental={rental} />
              : statusAction || <StatusBadge status={rental.status} />}
        </InlineStack>

        {fulfillmentAction || <InlineStack><Badge tone="info">{fulfillmentLabel(rental)}</Badge></InlineStack>}

        {rental.customerEmail && (
          <Text variant="bodySm" tone="subdued">{rental.customerEmail}</Text>
        )}

        {mode !== 'list' && (
          <BlockStack gap="100">
            <Text variant="bodySm">
              <strong>Period:</strong> {formatDate(rental.startDate)} &ndash; {formatDate(rental.endDate)}
            </Text>
            {mode === 'upcoming' ? (
              <Text variant="bodySm"><strong>Ship to:</strong> {formatAddress(rental)}</Text>
            ) : mode === 'active' ? (
              <Text variant="bodySm"><strong>Expected return:</strong> {formatDate(rental.endDate)}</Text>
            ) : null}
          </BlockStack>
        )}

        {mode === 'list' && (
          <Text variant="bodySm"><strong>Ship to:</strong> {formatAddress(rental)}</Text>
        )}

        <RentalItemThumbnails items={rental.rentalItems} />
        <InlineStack align="space-between" blockAlign="center" gap="300">
          <Button size="slim" variant="plain" onClick={() => onView(rental)}>
            View details
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
};

export default RentalOverviewCard;
